import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LandingPage } from './pages/LandingPage';
import { AuthPage } from './pages/AuthPage';
import { CustomerDashboard } from './pages/CustomerDashboard';
import { ContractorDashboard } from './pages/ContractorDashboard';
import { JobsListPage } from './pages/JobsListPage';
import { CreateJobPage } from './pages/CreateJobPage';
import { JobDetailPage } from './pages/JobDetailPage';
import { MessagesPage } from './pages/MessagesPage';
import { SubscriptionPage, SubscriptionSuccessPage } from './pages/SubscriptionPage';
import { ContractorProfilePage } from './pages/ContractorProfilePage';
import { PayoutDashboard } from './pages/PayoutDashboard';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import './App.css';

// Register service worker
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered:', registration.scope);
        })
        .catch((error) => {
          console.log('SW registration failed:', error);
        });
    });
  }
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Routes
const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={user ? <Navigate to={user.role === 'contractor' ? '/contractor/dashboard' : '/customer/dashboard'} /> : <AuthPage />} />
      <Route path="/jobs" element={<JobsListPage />} />
      <Route path="/jobs/:id" element={<JobDetailPage />} />

      {/* Customer Routes */}
      <Route 
        path="/customer/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/jobs/new" 
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CreateJobPage />
          </ProtectedRoute>
        } 
      />

      {/* Contractor Routes */}
      <Route 
        path="/contractor/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contractor/profile" 
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <ContractorProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription/success" 
        element={
          <ProtectedRoute allowedRoles={['contractor']}>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes (Both Roles) */}
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        } 
      />

      {/* Payment Success/Cancel */}
      <Route path="/payment/success" element={<Navigate to="/customer/dashboard" />} />

      {/* Admin Routes */}
      <Route path="/admin/payouts" element={<PayoutDashboard />} />

      {/* Catch All */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#1C1917',
              color: '#FAFAF9',
              border: '1px solid #44403C',
            },
          }}
        />
        <AppRoutes />
        <PWAInstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
